import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

export default function OfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const goOnline = () => { setIsOnline(true); toast.success('Back online'); };
    const goOffline = () => { setIsOnline(false); toast.error('You are offline'); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    updateQueueCount();
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  useEffect(() => {
    if (isOnline) {
      const count = getQueue().length;
      if (count > 0) syncQueue();
    }
  }, [isOnline]);

  const getQueue = () => {
    try {
      const raw = localStorage.getItem('majnu_offline_orders');
      if (raw) return JSON.parse(raw);
      // One-time migration: read any legacy entries and move them over
      const legacy = localStorage.getItem('qave_offline_orders');
      if (legacy) {
        localStorage.setItem('majnu_offline_orders', legacy);
        localStorage.removeItem('qave_offline_orders');
        return JSON.parse(legacy);
      }
      return [];
    } catch { return []; }
  };

  const updateQueueCount = () => setQueueCount(getQueue().length);

  const syncQueue = async () => {
    const queue = getQueue();
    if (queue.length === 0) return;
    setSyncing(true);
    try {
      await api.post('/orders/offline-sync', { orders: queue });
      localStorage.setItem('majnu_offline_orders', '[]');
      setQueueCount(0);
      toast.success(`${queue.length} offline orders synced`);
    } catch (err) {
      toast.error('Sync failed. Will retry.');
    } finally {
      setSyncing(false);
    }
  };

  if (isOnline && queueCount === 0) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 flex items-center justify-between text-xs font-sans ${isOnline ? 'bg-amber-500/20 border-b border-amber-500/30' : 'bg-red-500/20 border-b border-red-500/30'}`} data-testid="offline-banner">
      <div className="flex items-center gap-2">
        {isOnline ? <Wifi className="w-3 h-3 text-amber-400" /> : <WifiOff className="w-3 h-3 text-red-400" />}
        <span className={isOnline ? 'text-amber-400' : 'text-red-400'}>
          {isOnline ? `${queueCount} offline orders pending sync` : 'Offline - orders will be queued'}
        </span>
      </div>
      {isOnline && queueCount > 0 && (
        <Button size="sm" onClick={syncQueue} disabled={syncing} className="h-6 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-sm text-xs font-sans" data-testid="sync-button">
          <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync Now
        </Button>
      )}
    </div>
  );
}

// Helper to queue an order offline (call from CustomerPage when offline)
export function queueOfflineOrder(order) {
  try {
    const queue = JSON.parse(localStorage.getItem('majnu_offline_orders') || '[]');
    queue.push({ ...order, created_offline_at: new Date().toISOString() });
    localStorage.setItem('majnu_offline_orders', JSON.stringify(queue));
    return true;
  } catch { return false; }
}
